/**
 * REF-2 / REF-7 — sort and filter rules for the Refund Log.
 *
 * Pure functions, kept out of the component so the rules can be read and tested on their
 * own, and so the page can report filter state to telemetry without reaching into the table.
 */
import {
    feeRefundAmount,
    itemRefundAmount,
    taxRefundAmount,
    tipsAndGratuityRefundAmount,
    type RefundComponents,
    type RefundRecord,
    type RefundType,
} from './refundModel';

/* ─── Filters ───────────────────────────────────────────────────────────────── */

/** "Returned any of" — which refund components a row must carry to stay in view. */
export type RefundComponentFilter = 'item' | 'tax' | 'tipsAndGratuity' | 'fees';

export const COMPONENT_FILTER_LABEL: Record<RefundComponentFilter, string> = {
    item: 'Item with tax',
    tax: 'Tax',
    tipsAndGratuity: 'Tips & Gratuity',
    fees: 'Fees',
};

const COMPONENT_AMOUNT: Record<RefundComponentFilter, (c: RefundComponents) => number> = {
    item: itemRefundAmount,
    tax: taxRefundAmount,
    tipsAndGratuity: tipsAndGratuityRefundAmount,
    fees: feeRefundAmount,
};

export interface RefundLogFilters {
    /** Order no. ('#' optional), reason, staff, check owner, guest or channel. Case-insensitive. */
    search: string;
    types: RefundType[];
    /** Literal reason text (e.g. 'Customer Request'), from GET /refund-log-filters?type=reason. */
    reasons: string[];
    /** Who PROCESSED the refund — the Staff column. */
    staff: string[];
    channels: string[];
    components: RefundComponentFilter[];
}

/** Which filter just changed; `clear` for Clear all. Lets the page skip per-keystroke telemetry. */
export type RefundLogFilterChange = keyof RefundLogFilters | 'clear';

export const EMPTY_REFUND_LOG_FILTERS: RefundLogFilters = {
    search: '',
    types: [],
    reasons: [],
    staff: [],
    channels: [],
    components: [],
};

export function activeFilterCount(f: RefundLogFilters): number {
    return (f.search.trim() ? 1 : 0)
        + f.types.length + f.reasons.length + f.staff.length + f.channels.length + f.components.length;
}

/**
 * AND across dimensions, OR within one: ticking Fee and Full under Type, Karthi under Staff
 * and Tax under Returned means "Fee or Full refunds, processed by Karthi, that returned tax" —
 * the reading an operator means when they tick two boxes in one menu and one in another.
 *
 * `search`, `types`, `reasons`, `staff` and `channels` are NOT applied here — they are sent to
 * `/refund-log` as `search` / `type` / `reason` / `staff` / `channel` and the rows this runs on
 * already come back server-scoped to them (see useRefundLogTableQuery.ts). Those fields stay on
 * `RefundLogFilters` for the controls' state, chips and telemetry. Only `components` (Returned)
 * has no server equivalent, so it alone keeps narrowing client-side on top of the server-scoped rows.
 */
export function filterRefundLog(records: RefundRecord[], f: RefundLogFilters): RefundRecord[] {
    return records.filter((r) => {
        if (f.components.length && !f.components.some((k) => COMPONENT_AMOUNT[k](r.components) > 0)) return false;
        return true;
    });
}

/* ─── Sort ──────────────────────────────────────────────────────────────────── */

export type RefundLogSortKey =
    | 'orderNo' | 'refundedAt' | 'type' | 'reason' | 'staffName'
    | 'item' | 'tax' | 'tipsAndGratuity' | 'convenienceFee' | 'deliveryFee' | 'serviceCharge' | 'cardProcessingFee' | 'total';

export type SortDir = 'asc' | 'desc';

export interface RefundLogSort {
    key: RefundLogSortKey;
    dir: SortDir;
}

/** Newest refund first — the order a manager scans a log in. */
export const DEFAULT_REFUND_LOG_SORT: RefundLogSort = { key: 'refundedAt', dir: 'desc' };

/**
 * The direction a column sorts in on its FIRST click: money and dates largest / newest
 * first, text A→Z. Clicking the active column again flips it.
 */
export const FIRST_CLICK_DIR: Record<RefundLogSortKey, SortDir> = {
    orderNo: 'asc',
    refundedAt: 'desc',
    type: 'asc',
    reason: 'asc',
    staffName: 'asc',
    item: 'desc',
    tax: 'desc',
    tipsAndGratuity: 'desc',
    convenienceFee: 'desc',
    deliveryFee: 'desc',
    serviceCharge: 'desc',
    cardProcessingFee: 'desc',
    total: 'desc',
};

/**
 * FE sort key → the `/refund-log` response field `sortBy` names, per REF-2's real fields
 * (`RefundLogItem` in clickHouseInterface.ts). Sorting itself now happens server-side —
 * `useRefundLogTableQuery` sends this alongside `sortDir`, so the rows it returns already
 * come back in order and no client-side comparator runs on them.
 */
export const REFUND_LOG_SORT_FIELD: Record<RefundLogSortKey, string> = {
    orderNo: 'orderNo',
    refundedAt: 'refundDate',
    type: 'refundType',
    reason: 'reason',
    staffName: 'staffName',
    item: 'itemsRefunded',
    tax: 'taxRefunded',
    tipsAndGratuity: 'tipsAndGratRefunded',
    convenienceFee: 'convenienceFeeRefunded',
    deliveryFee: 'deliveryRefunded',
    serviceCharge: 'serviceChargeRefunded',
    cardProcessingFee: 'cardProcessingFeeRefunded',
    total: 'refunded',
};

/** Mobile has no sortable headers, so the useful sorts are offered as a single menu. */
export const MOBILE_SORTS: Array<{ id: string; label: string; sort: RefundLogSort }> = [
    { id: 'newest', label: 'Newest first', sort: { key: 'refundedAt', dir: 'desc' } },
    { id: 'oldest', label: 'Oldest first', sort: { key: 'refundedAt', dir: 'asc' } },
    { id: 'high', label: 'Refunded: high to low', sort: { key: 'total', dir: 'desc' } },
    { id: 'low', label: 'Refunded: low to high', sort: { key: 'total', dir: 'asc' } },
    { id: 'items', label: 'Item with tax: high to low', sort: { key: 'item', dir: 'desc' } },
    { id: 'order', label: 'Order no.', sort: { key: 'orderNo', dir: 'asc' } },
    { id: 'staff', label: 'Staff A–Z', sort: { key: 'staffName', dir: 'asc' } },
    { id: 'type', label: 'Refund type', sort: { key: 'type', dir: 'asc' } },
];
